// Lightweight JSON to TOON converter - zero dependencies, optimized performance

export interface ConversionResult {
  toon: string;
  originalTokens: number;
  toonTokens: number;
  savingsPercent: number;
  error?: string;
}

/**
 * Converts JSON to TOON format (Token-Oriented Object Notation)
 * Eliminates redundant syntax while preserving structure through indentation
 */
export function jsonToToon(jsonString: string): ConversionResult {
  try {
    // Validate and parse JSON
    const parsed = JSON.parse(jsonString);

    // Convert to TOON format
    const toon = convertToToon(parsed, 0).trim();

    // Calculate token counts (approximate: 1 token ≈ 4 characters)
    const originalTokens = Math.ceil(jsonString.length / 4);
    const toonTokens = Math.ceil(toon.length / 4);
    const savingsPercent = Math.round(((originalTokens - toonTokens) / originalTokens) * 100);

    return {
      toon,
      originalTokens,
      toonTokens,
      savingsPercent: Math.max(0, savingsPercent)
    };
  } catch (error) {
    return {
      toon: '',
      originalTokens: 0,
      toonTokens: 0,
      savingsPercent: 0,
      error: error instanceof Error ? error.message : 'Invalid JSON'
    };
  }
}

/**
 * Recursively converts parsed JSON to TOON format
 */
function convertToToon(value: any, depth: number): string {
  const indent = '  '.repeat(depth);

  if (value === null) {
    return 'null';
  }

  if (typeof value === 'boolean' || typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'string') {
    // Only quote strings with special characters or whitespace
    if (/^[a-zA-Z0-9_-]+$/.test(value)) {
      return value;
    }
    return `"${value.replace(/"/g, '\\"')}"`;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '[]';
    }

    // Inline simple arrays
    if (value.every(v => typeof v !== 'object' || v === null)) {
      const items = value.map(v => convertToToon(v, 0)).join(' ');
      if (items.length < 60) {
        return `[${items}]`;
      }
    }

    // Multi-line arrays
    const items = value.map((v, idx) => {
      const converted = convertToToon(v, depth + 1);
      // For nested objects/arrays in array items
      if (converted.includes('\n')) {
        const lines = converted.split('\n');
        // Trim the first line to remove extra spacing before adding the dash
        return `${indent}- ${lines[0].trim()}\n${lines.slice(1).join('\n')}`;
      }
      return `${indent}- ${converted}`;
    }).join('\n');

    return items;
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);

    if (entries.length === 0) {
      return '{}';
    }

    const props = entries.map(([key, val]) => {
      const needsQuotes = !/^[a-zA-Z0-9_-]+$/.test(key);
      const formattedKey = needsQuotes ? `"${key}"` : key;
      const converted = convertToToon(val, depth + 1);

      // Inline simple values
      if (typeof val !== 'object' || val === null || (Array.isArray(val) && !converted.includes('\n'))) {
        return `${indent}${formattedKey}: ${converted}`;
      }

      // Multi-line complex values
      const lines = converted.split('\n');
      if (lines.length > 1) {
        return `${indent}${formattedKey}:\n${converted}`;
      }

      return `${indent}${formattedKey}: ${converted}`;
    }).join('\n');

    return props;
  }

  return String(value);
}

/**
 * Converts TOON back to JSON format
 */
export function toonToJson(toonString: string): ConversionResult {
  try {
    const parsed = parseToon(toonString);
    const json = JSON.stringify(parsed, null, 2);

    const originalTokens = Math.ceil(toonString.length / 4);
    const jsonTokens = Math.ceil(json.length / 4);
    const difference = jsonTokens - originalTokens;

    return {
      toon: json,
      originalTokens,
      toonTokens: jsonTokens,
      savingsPercent: difference,
      error: undefined
    };
  } catch (error) {
    return {
      toon: '',
      originalTokens: 0,
      toonTokens: 0,
      savingsPercent: 0,
      error: error instanceof Error ? error.message : 'Invalid TOON format'
    };
  }
}

/**
 * TOON parser with proper indentation support
 */
function parseToon(toon: string): any {
  const lines = toon.split('\n');

  // Parse with indentation tracking
  const result = parseLines(lines, 0);
  return result.value;
}

interface ParseResult {
  value: any;
  nextIndex: number;
}

function parseLines(lines: string[], startIndex: number, baseIndent: number = 0): ParseResult {
  if (startIndex >= lines.length) {
    return { value: {}, nextIndex: startIndex };
  }

  const firstLine = lines[startIndex];
  const trimmed = firstLine.trim();

  // Check if it's an array item
  if (trimmed.startsWith('-')) {
    return parseArray(lines, startIndex, baseIndent);
  }

  // Parse as object
  const obj: any = {};
  let i = startIndex;

  while (i < lines.length) {
    const line = lines[i];
    const indent = getIndent(line);
    const content = line.trim();

    if (!content || indent < baseIndent) {
      break;
    }

    if (indent > baseIndent) {
      // This belongs to previous property, skip
      i++;
      continue;
    }

    // Parse key-value pair
    const colonIndex = content.indexOf(':');
    if (colonIndex > 0) {
      const key = content.slice(0, colonIndex).trim().replace(/^"(.*)"$/, '$1');
      const valueStr = content.slice(colonIndex + 1).trim();

      if (valueStr) {
        // Inline value
        obj[key] = parseValue(valueStr);
        i++;
      } else {
        // Check next line for nested content
        if (i + 1 < lines.length) {
          const nextIndent = getIndent(lines[i + 1]);
          if (nextIndent > indent) {
            // Has nested content
            const result = parseLines(lines, i + 1, nextIndent);
            obj[key] = result.value;
            i = result.nextIndex;
          } else {
            // Empty value
            obj[key] = null;
            i++;
          }
        } else {
          obj[key] = null;
          i++;
        }
      }
    } else {
      i++;
    }
  }

  return { value: obj, nextIndex: i };
}

function parseArray(lines: string[], startIndex: number, baseIndent: number): ParseResult {
  const arr: any[] = [];
  let i = startIndex;
  const firstIndent = getIndent(lines[i]);

  while (i < lines.length) {
    const line = lines[i];
    const indent = getIndent(line);
    const content = line.trim();

    if (!content || indent < baseIndent) {
      break;
    }

    if (indent < firstIndent) {
      break;
    }

    if (content.startsWith('-')) {
      const afterDash = content.slice(1).trim();

      if (afterDash) {
        // Content after dash - could be a key:value pair or a simple value
        const colonIndex = afterDash.indexOf(':');

        if (colonIndex > 0) {
          // It's a key-value pair, start building an object
          const obj: any = {};
          const key = afterDash.slice(0, colonIndex).trim().replace(/^"(.*)"$/, '$1');
          const valueStr = afterDash.slice(colonIndex + 1).trim();
          obj[key] = valueStr ? parseValue(valueStr) : null;

          // Check for more properties on subsequent lines
          i++;
          while (i < lines.length) {
            const nextLine = lines[i];
            const nextIndent = getIndent(nextLine);
            const nextContent = nextLine.trim();

            // Stop if we hit another array item or dedent
            if (!nextContent || nextIndent <= indent || nextContent.startsWith('-')) {
              break;
            }

            // Parse this property
            const propColonIndex = nextContent.indexOf(':');
            if (propColonIndex > 0) {
              const propKey = nextContent.slice(0, propColonIndex).trim().replace(/^"(.*)"$/, '$1');
              const propValueStr = nextContent.slice(propColonIndex + 1).trim();

              if (propValueStr) {
                obj[propKey] = parseValue(propValueStr);
                i++;
              } else {
                // Check if next line has nested content
                if (i + 1 < lines.length) {
                  const nestedIndent = getIndent(lines[i + 1]);
                  if (nestedIndent > nextIndent) {
                    const result = parseLines(lines, i + 1, nestedIndent);
                    obj[propKey] = result.value;
                    i = result.nextIndex;
                  } else {
                    obj[propKey] = null;
                    i++;
                  }
                } else {
                  obj[propKey] = null;
                  i++;
                }
              }
            } else {
              i++;
            }
          }

          arr.push(obj);
        } else {
          // Simple value after dash
          arr.push(parseValue(afterDash));
          i++;
        }
      } else {
        // No content after dash, check next line for nested content
        if (i + 1 < lines.length) {
          const nextIndent = getIndent(lines[i + 1]);
          if (nextIndent > indent) {
            const result = parseLines(lines, i + 1, nextIndent);
            arr.push(result.value);
            i = result.nextIndex;
          } else {
            arr.push(null);
            i++;
          }
        } else {
          arr.push(null);
          i++;
        }
      }
    } else {
      break;
    }
  }

  return { value: arr, nextIndex: i };
}

function parseValue(str: string): any {
  str = str.trim();

  // null
  if (str === 'null') return null;

  // boolean
  if (str === 'true') return true;
  if (str === 'false') return false;

  // number
  if (/^-?\d+\.?\d*$/.test(str)) return Number(str);

  // quoted string
  if (str.startsWith('"') && str.endsWith('"')) {
    return str.slice(1, -1).replace(/\\"/g, '"');
  }

  // inline array
  if (str.startsWith('[') && str.endsWith(']')) {
    const content = str.slice(1, -1).trim();
    if (!content) return [];

    // Split by spaces but respect quoted strings
    const items: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < content.length; i++) {
      const char = content[i];

      if (char === '"') {
        inQuotes = !inQuotes;
        current += char;
      } else if (char === ' ' && !inQuotes) {
        if (current) {
          items.push(current);
          current = '';
        }
      } else {
        current += char;
      }
    }

    if (current) {
      items.push(current);
    }

    return items.map(item => parseValue(item));
  }

  // unquoted string
  return str;
}

function getIndent(line: string): number {
  let count = 0;
  for (let i = 0; i < line.length; i++) {
    if (line[i] === ' ') {
      count++;
    } else {
      break;
    }
  }
  return count;
}

/**
 * Validates JSON string
 */
export function validateJson(jsonString: string): { valid: boolean; error?: string } {
  if (!jsonString.trim()) {
    return { valid: false, error: 'Input is empty' };
  }

  try {
    JSON.parse(jsonString);
    return { valid: true };
  } catch (error) {
    if (error instanceof SyntaxError) {
      return {
        valid: false,
        error: `JSON syntax error: ${error.message}`
      };
    }
    return { valid: false, error: 'Invalid JSON format' };
  }
}

/**
 * Formats/beautifies JSON string
 */
export function formatJson(jsonString: string): string {
  try {
    const parsed = JSON.parse(jsonString);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return jsonString;
  }
}
